import { LocalDate } from '@spa-frontend/date-lib';
import { computed, observable } from 'mobx';
import React, { RefObject } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { component, initialize, inject } from 'tsdi';
import { wrapRequest } from 'wrap-request';

import { Api } from '../../core/api';
import {
    CancelContractManualRequestDto,
    CancelContractRequestDto,
    GetActiveContractsRequestDto,
    GetActiveContractsResponseDto
} from '../../core/api/dtos';
import { Field, Form } from '../../core/common/forms';
import { required, requiredIf } from '../../core/common/validation';
import { SharedContractDissolutionStore } from '../shared-contract-dissolution-store';

@component
export class ContractTerminationStore {
    @inject
    private api!: Api;

    @inject
    private sharedStore!: SharedContractDissolutionStore;

    @observable
    private _contractToTerminateId?: number;

    @observable
    private _hasContracts = false;

    public readonly showContractsRequest = wrapRequest(
        ({
            recaptchaToken,
            memberData
        }: {
            recaptchaToken: string;
            memberData: GetActiveContractsRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.getActiveContracts(
                { recaptchaToken },
                memberData
            )
    );

    public readonly cancelContractRequest = wrapRequest(
        ({
            recaptchaToken,
            cancelContractDto
        }: {
            recaptchaToken: string;
            cancelContractDto: CancelContractRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.cancelContract(
                { recaptchaToken },
                cancelContractDto
            )
    );

    public readonly cancelContractManualRequest = wrapRequest(
        ({
            recaptchaToken,
            cancelContractManualDto
        }: {
            recaptchaToken: string;
            cancelContractManualDto: CancelContractManualRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.cancelContractManual(
                { recaptchaToken },
                cancelContractManualDto
            )
    );

    private cancelContractForms: Form<CancelContractRequestDto>[] = [];

    private readonly cancellationDateTypeField = new Field<
        CancelContractManualRequestDto['cancellationDateType']
    >('NEXT_POSSIBLE_CANCELLATION_DATE', required());

    private readonly cancellationTypeField = new Field<
        CancelContractManualRequestDto['cancellationType']
    >('ORDINARY_CANCELLATION', required());

    public readonly cancelContractManualForm: Form<CancelContractManualRequestDto> =
        new Form<CancelContractManualRequestDto>({
            firstname: new Field<string>('', required()),
            lastname: new Field<string>('', required()),
            dateOfBirth: new Field<LocalDate>(LocalDate.now(), required()),
            customerNumber: new Field<string | undefined>(undefined),
            studioId: new Field<number | undefined>(undefined, required()),
            additionalInformation: new Field<string | undefined>(undefined),
            confirmationEmail: new Field<string | undefined>(undefined),
            cancelationReasonId: new Field<number | undefined>(
                undefined,
                requiredIf(
                    () =>
                        this.cancellationTypeField.value ===
                        'EXTRAORDINARY_CANCELLATION'
                )
            ),
            cancellationDateType: this.cancellationDateTypeField,
            cancellationDate: new Field<LocalDate | undefined>(
                undefined,
                requiredIf(
                    () =>
                        this.cancellationDateTypeField.value ===
                        'ABSOLUTE_CANCELLATION_DATE'
                )
            ),
            cancellationType: this.cancellationTypeField
        });

    @initialize
    protected init = async () => {
        await this.sharedStore.prefillSelectedStudio(
            this.cancelContractManualForm.wrappedField.studioId
        );
    };

    @computed
    public get hasContracts(): boolean {
        return this._hasContracts;
    }

    @computed
    public get contractToTerminateId(): number | undefined {
        return this._contractToTerminateId;
    }

    public getCancelContractForm = (contractId: number | undefined) =>
        this.cancelContractForms.find(
            (form) => form.wrappedField.contractId.value === contractId
        );

    public addCancelContractForm = (
        contract: GetActiveContractsResponseDto
    ) => {
        const customerNumber =
            this.sharedStore.memberDataForm.wrappedField.customerNumber.value;
        const firstPossibleCancellationDate = contract.cancellationDates
            ? contract.cancellationDates[0]
            : undefined;
        if (
            contract.contractId &&
            customerNumber &&
            firstPossibleCancellationDate &&
            !this.cancelContractForms.some(
                (form) =>
                    form.wrappedField.contractId.value === contract.contractId
            )
        ) {
            const cancellationTypeField = new Field<
                CancelContractRequestDto['cancellationType']
            >('ORDINARY_CANCELLATION', required());
            this.cancelContractForms.push(
                new Form<CancelContractRequestDto>({
                    firstname: new Field<string>(
                        this.sharedStore.memberDataForm.wrappedField.firstname.value,
                        required()
                    ),
                    lastname: new Field<string>(
                        this.sharedStore.memberDataForm.wrappedField.lastname.value,
                        required()
                    ),
                    dateOfBirth: new Field<LocalDate>(
                        this.sharedStore.memberDataForm.wrappedField.dateOfBirth.value,
                        required()
                    ),
                    customerNumber: new Field<string>(
                        customerNumber,
                        required()
                    ),
                    contractId: new Field<number>(
                        contract.contractId,
                        required()
                    ),
                    cancellationType: cancellationTypeField,
                    cancellationDate: new Field<LocalDate>(
                        firstPossibleCancellationDate,
                        required()
                    ),
                    cancelationReasonId: new Field<number | undefined>(
                        undefined,
                        requiredIf(
                            () =>
                                cancellationTypeField.value ===
                                'EXTRAORDINARY_CANCELLATION'
                        )
                    ),
                    confirmationEmail: new Field<string | undefined>(''),
                    additionalInformation: new Field<string | undefined>('')
                })
            );
        }
    };

    public handleMemberDataSubmit = async (
        recaptchaRef: React.RefObject<ReCAPTCHA>
    ) => {
        const memberData =
            await this.sharedStore.memberDataForm.getValidModel();
        if (!memberData) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            recaptchaRef.current?.reset();
            return;
        }
        await this.showContractsRequest.request({
            recaptchaToken,
            memberData
        });

        const studioId =
            this.showContractsRequest.result?.length &&
            this.showContractsRequest.result[0].studioId;

        if (
            studioId &&
            this.sharedStore.studios?.some((studio) => studio.id === studioId)
        ) {
            await this.sharedStore.getStudioInfo.request(studioId);
        }

        this._hasContracts = this.showContractsRequest.state === 'fetched';
        recaptchaRef.current?.reset();
    };

    public handleTerminate = async (
        recaptchaRef: RefObject<ReCAPTCHA>,
        contractId: number
    ) => {
        this._contractToTerminateId = contractId;
        const memberFieldsValid =
            await this.sharedStore.memberDataForm.validate();
        const cancelContractDto =
            await this.getCancelContractForm(contractId)?.getValidModel();
        if (!memberFieldsValid || !cancelContractDto) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            recaptchaRef.current?.reset();
            return;
        }
        await this.cancelContractRequest.request({
            recaptchaToken,
            cancelContractDto
        });
        recaptchaRef.current?.reset();
    };

    public handleTerminateManual = async (
        recaptchaRef: RefObject<ReCAPTCHA>
    ) => {
        const memberFieldsValid =
            await this.sharedStore.memberDataForm.validate();
        this.sharedStore.copyMemberDataToFields(
            this.cancelContractManualForm.wrappedField
        );
        const cancelContractManualDto =
            await this.cancelContractManualForm?.getValidModel();
        if (!memberFieldsValid || !cancelContractManualDto) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            return;
        }
        await this.cancelContractManualRequest.request({
            recaptchaToken,
            cancelContractManualDto
        });
        recaptchaRef.current?.reset();
    };
}
